<!-- Part of the SPARKL educational activity system, Copyright 2019 by Pepper Williams -->
<template>
	<div>
		<div class="k-main-division-wrapper k-main-welcome">
			<div class="k-main-welcome__headline d-flex align-center mb-10">
				<k-spacer/>
				<div class="k-main-welcome__question k-shadow-text mr-8"><b>{{welcome_msg}}</b></div>
				<div class="k-main-welcome__time-date k-shadow-text" v-html="time_string"></div>
				<k-spacer/>
			</div>

			<div
				class="k-main-welcome--nav-btns"
				role="tablist"
				aria-label="Welcome sections"
			>
				<k-tooltip v-for="(tab, i) in tabs" :key="tab.id" bottom>
					<template v-slot:activator="{on}">
						<k-btn
							v-on="on"
							class="k-main-welcome--nav-btn"
							:class="{ 'k-main-welcome--nav-btn-current': selected_tab == tab.id }"
							type="button"
							role="tab"
							:id="`welcome-tab-${tab.id}`"
							aria-controls="welcome-tabpanel"
							:aria-selected="String(selected_tab === tab.id)"
							:tabindex="selected_tab === tab.id ? 0 : -1"
							:aria-label="tab.title"
							@click="tab_selected(tab.id)"
							@keydown="onTabKeydown($event, i)"
						>
							<k-icon aria-hidden="true">
								{{ tab.icon }}
							</k-icon>
						</k-btn>
					</template>
					
					{{ tab.title }}
				</k-tooltip>
			</div>

			<div 
				class="k-main-welcome--pages"
				role="tabpanel"
				id="welcome-tabpanel"
				:aria-labelledby="`welcome-tab-${selected_tab}`"
				tabindex="0"
			>
				<HomeHome v-if="welcome_section_showing == 'home'" :config="tab_data.home"/>
				<AdminList v-else-if="welcome_section_showing == 'admin'" :config="tab_data.admin"/>
				<FamilyHome v-else-if="welcome_section_showing == 'family_home'"/>
				<SupportPage v-else-if="welcome_section_showing == 'support_page'"/>
				<ResourceUsage v-else-if="welcome_section_showing == 'resource_usage'"/>
				<AssetManager v-else-if="welcome_section_showing == 'asset_manager'"/>
				<div v-else-if="welcome_section_showing !== 'standards'">
					<FavoritesIndex v-if="index_view_flavor == 'favorites'"/>
					<div v-else>
						<CourseIndex ref="lp_index_component" v-if="welcome_section_showing == 'classes'" :config="tab_data.classes"/>
						<RepositoriesIndex v-if="welcome_section_showing == 'resourcerepos'" :config="tab_data.resourcerepos"/>
						<PDIndex v-if="welcome_section_showing == 'pd'" :config="tab_data.pd"/>
						<MyCollectionsIndex v-if="welcome_section_showing == 'mycollections'" :config="tab_data.mycollections"/>
					</div>
				</div>
				<!-- We want the standards page to always be showing, because once we've loaded Satchel in the iframe there, we want it to stay loaded, wherever the user last left it -->
				<StandardsHomeSatchel v-show="welcome_section_showing == 'standards'" :config="tab_data.standards"/>
			</div>
		</div>
	</div>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import HomeHome from './../home/HomeHome'
import FamilyHome from './../home/FamilyHome'
import StandardsHomeSatchel from './../standards/StandardsHomeSatchel'
import AdminList from './../admin/AdminList'
import CourseIndex from './../collectionlist/CourseIndex'
import FavoritesIndex from './../collectionlist/FavoritesIndex'
import RepositoriesIndex from './../collectionlist/RepositoriesIndex'
import MyCollectionsIndex from './../collectionlist/MyCollectionsIndex'
import PDIndex from './../collectionlist/PDIndex'
import ResourceUsage from './ResourceUsage'
import AssetManager from '../assetManager/AssetManager.vue'
import SupportPage from '../home/SupportPage.vue'

export default {
	components: {
		HomeHome,
		FavoritesIndex,
		CourseIndex,
		StandardsHomeSatchel,
		AdminList,
		RepositoriesIndex,
		MyCollectionsIndex,
		PDIndex,
		FamilyHome,
		ResourceUsage,
		AssetManager,
		SupportPage
	},
	
	data() { return {
		time_string: '',
		time_string_colon: true,
		welcome_msg: 'Welcome!',
		classes_initialized: false,
		show_all_announcements: false,
		debug_uuid: U.new_uuid(),
		initial_classes_flavor_shown: false,	// see below
		// these are for the "mini-case-tree"
		case_tree_loaded: false,
		case_framework_identifier: '',
		case_search_terms: '',
		selected_tab: 'home',
	}},

	computed: {
		...mapState(['user_info', 'site_config']),

		...mapGetters(['signed_in', 'studentish_role', 'role']),

		role() { return this.user_info.role },

		index_view_flavor: {
			get() { 
				if (!this.signed_in) return this.$store.state.lst.unsigned_index_view_flavor
				else {
					if (this.welcome_section_showing == 'classes') return this.$store.state.lst.course_index_view_flavor
					else if (this.welcome_section_showing == 'mycollections') return this.$store.state.lst.my_index_view_flavor
					else return this.$store.state.lst.repo_index_view_flavor
				}
			},
			set(val) { 
				this.$store.commit('lst_set', ['index_view_flavor', val]) 
				if (!this.signed_in) this.$store.commit('lst_set', ['unsigned_index_view_flavor', val])
				else {
					if (this.welcome_section_showing == 'classes') this.$store.commit('lst_set', ['course_index_view_flavor', val])
					else if (this.welcome_section_showing == 'mycollections') this.$store.commit('lst_set', ['my_index_view_flavor', val])
					else this.$store.commit('lst_set', ['repo_index_view_flavor', val])
				}
			}
		},

		show_pd() {
			if (this.site_config.show_pd === 'no') return false
			return true
		},

		welcome_section_showing: {
			get() { return this.$store.state.lst.welcome_section_showing },
			set(val) { this.$store.commit('lst_set', ['welcome_section_showing', val]) }
		},

		tab_data() {
			let customized_tabs = this.site_config.customized_tabs

			let defaults = {
				home: {
					id: 'home',
					icon: customized_tabs.home?.icon || (this.role === 'parent' ? 'fa-home-user' : 'fa-home'),
					title: customized_tabs.home?.title || `${this.site_config.app_name} Home`
				},
				classes: {
					id: 'classes',
					icon: customized_tabs.classes?.icon || 'fas fa-chalkboard',
					title: customized_tabs.classes?.title || 'Courses'
				},
				resourcerepos: {
					id: 'resourcerepos',
					icon: customized_tabs.resourcerepos?.icon || 'fas fa-diagram-project',
					title: customized_tabs.resourcerepos?.title || this.site_config.resourcerepos_noun
				},
				pd: {
					id: 'pd',
					icon: customized_tabs.pd?.icon || 'fas fa-user-graduate',
					title: customized_tabs.pd?.title || 'Professional Development'
				},
				mycollections: {
					id: 'mycollections',
					icon: customized_tabs.mycollections?.icon || 'fas fa-cubes-stacked',
					title: customized_tabs.mycollections?.title || 'My Content Collections'
				},
				standards: {
					id: 'standards',
					icon: customized_tabs.standards?.icon || 'fas fa-map',
					title: customized_tabs.standards?.title || 'Standards'
				},
				admin: {
					id: 'admin',
					icon: customized_tabs.admin?.icon || 'fas fa-cog',
					title: customized_tabs.admin?.title || 'Site Admin Tools'
				},
			}

			Object.keys(defaults).forEach(tab => {
				switch(tab) {
					case 'admin':
						// don't show admin tab on small screens
						if (this.$vuetify.breakpoint.xs || this.$vuetify.breakpoint.sm || (this.role != 'admin' && !vapp.has_admin_right('subdomain_admin'))) {
							delete defaults.admin
						}
						break
					case 'mycollections':
						if (!(this.signed_in || this.site_config.show_mycollections_when_not_signed_in == 'yes') || this.studentish_role) {
							delete defaults.mycollections
						}
						break
					default:
						if (!this.main_tabs_to_include.includes(tab)) {
							delete defaults[tab]
						}
				}
			})

			return defaults
		},

		tabs() {	
			return Object.values(this.tab_data)
		},

		main_tabs_to_include() {
			if (this.site_config.main_tabs_to_include != 'all') return this.site_config.main_tabs_to_include
			return ['home', 'classes', 'resourcerepos', 'mycollections', 'standards']
		},
	},

	watch: {
		'$route.path': {immediate: true, handler(val) {
			// when the route changes, change welcome_section_showing accordingly
			let s
			// if (this.$route.path.indexOf('home') == 1) s = this.role === 'parent' ? 'family_home' : 'home'
			if (this.$route.path.indexOf('courses') == 1) s = 'classes'
			else if (this.$route.path.indexOf('standards') == 1) s = 'standards'
			else if (this.$route.path.indexOf('admin') == 1) s = 'admin'
			else if (this.$route.path.indexOf('repos') == 1) s = 'resourcerepos'
			else if (this.$route.path.indexOf('mycollections') == 1) s = 'mycollections'
			else if (this.$route.path.indexOf('pd') == 1) s = 'pd'
			else if (this.$route.path.indexOf('support') == 1) s = 'support_page'
			else if (this.$route.path.indexOf('asset_manager') == 1 && this.signed_in) s = 'asset_manager'
			// If user is not superuser but has been granted accesss to resource report, we need a non-admin route
			else if (this.$route.path.indexOf('resource_usage') == 1 && this.$route.path.indexOf('admin') == -1) s = 'resource_usage'

			// default: go to home
			else {
				// if we're explicitly sent to the home route and if we're showing the home page in a dialog, go to the first tab and show the home dialog
				if (this.site_config.show_pop_up) {
					s = this.site_config.main_tabs_to_include[0]
					if (this.$route.path.indexOf('home') == 1) vapp.show_home_dialog = true
				} else {
					s = this.role === 'parent' ? 'family_home' : 'home'
				}
				
			}
			
			this.selected_tab = s
			this.welcome_section_showing = s
		}},
	},

	created() {
		this.calculate_time_string();

		setInterval(() => {
			this.calculate_time_string();
		}, 1000)

		// for the courses tab, we normally default to favorites; but if the user doesn't have any sis_classes or added_my_courses, always start with the index
		if (this.signed_in && !this.initial_classes_flavor_shown && this.$store.state.sis_classes.length == 0 && this.$store.state.added_my_courses.length == 0) {
			this.$store.commit('lst_set', ['course_index_view_flavor', 'lpindex'])
			this.initial_classes_flavor_shown = true
		}

		if (!empty(this.subdomain)) console.warn('subdomain: ' + this.subdomain)
	},

	mounted() {
		vapp.welcome_view = this
	},
	
	methods: {
		onTabKeydown(e, index) {
			const keys = ['ArrowLeft', 'ArrowRight', 'Home', 'End']
			if (!keys.includes(e.key)) return

			e.preventDefault()

			const last = this.tabs.length - 1
			let next_index = index
			switch (e.key) {
				case 'ArrowLeft':
					next_index = index === 0
						? last
						: index - 1
					break
				case 'ArrowRight':
					next_index = index === last
						? 0
						: index + 1
					break
				case 'Home':
					next_index = 0
					break
				case 'End':
					next_index = last
					break
			}

			const next_tab = this.tabs[next_index]
			this.tab_selected(next_tab.id)

			this.$nextTick(() => {
				const el = document.getElementById(`welcome-tab-${next_tab.id}`)
				el?.focus()
			})
		},

		tab_selected(t) {
			this.selected_tab = t
			this.switch_welcome_section(t)
		},

		switch_welcome_section(s) {
			// to switch the welcome section, just go to the corresponding route; the route.path watcher will set welcome_section_showing accordingly
			if (s == 'home') this.go_to_route('home')
			else if (s == 'classes') this.go_to_route('courses')
			else if (s == 'standards') this.go_to_route('standards')
			// else if (s == 'resourcerepo') this.go_to_route('trl')
			else if (s == 'admin') this.go_to_route('admin')
			else if (s == 'resourcerepos') this.go_to_route('repos')
			else if (s == 'mycollections') this.go_to_route('mycollections')
			else if (s == 'resource_usage') this.go_to_route('resource_usage')
			else if (s == 'pd') this.go_to_route('pd')
		},

		go_to_route(new_route, event) {
			if (!empty(event) && !empty(event.target)) $(event.target).closest('button').blur()

			new_route = '/' + new_route
			if (this.$route.path == new_route) return

			this.$router.push({ path: new_route })
		},

		calculate_time_string() {
			// this.time_string_colon = !this.time_string_colon
			let d = new Date()

			let hour = d.getHours()

			let time_of_day
			if (hour > 2 && hour < 12) time_of_day = 'morning'
			else if (hour < 18) time_of_day = 'afternoon'
			else time_of_day = 'evening'

			this.welcome_msg = 'Good ' + time_of_day
			if (this.user_info.first_name) this.welcome_msg += ', ' + this.user_info.first_name
			this.welcome_msg += '!'

			hour = hour % 12
			if (hour == 0) hour = 12

			let minute = d.getMinutes()
			if (minute < 10) minute = '0' + minute

			// Thu Jan 29&nbsp;&nbsp;10:50
			this.time_string = sr(
				'It’s&nbsp;$4<span style="$5">:</span>$6&nbsp;on&nbsp;$1, $2 $3',
				['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'][d.getDay()],
				['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'][d.getMonth()],
				d.getDate(),
				hour,
				this.time_string_colon ? '' : 'visibility:hidden',
				minute,
			)
		},

		// PW: not currently used; do make this work properly we might need to switch the "CourseIndex" to v-show instead of v-if
		search_lps(search_string) {
			this.switch_welcome_section('classes')
			this.$nextTick(() => {
				this.$refs.lp_index_component.external_search(search_string)
			})
		},
	}
}
</script>

<style lang="scss">
.k-main-welcome {
	text-align: center;

	.k-main-welcome__headline {
		display: inline-flex;
		width: auto;
		max-width: max-content;
		margin: 0 auto;
		padding: 10px 20px;
		border-radius: 18px;
		background: rgba(0, 0, 0, 0.42);
		box-shadow: 0 6px 18px rgba(0, 0, 0, 0.22);
		backdrop-filter: blur(2px);
		-webkit-backdrop-filter: blur(2px);
	}

	.k-main-welcome__question {
		font-size: 35px;
		line-height: 45px;
		font-weight: bold;
		color: #ffe5d2;
	}

	.k-main-welcome__time-date {
		font-family: $display-font;
		font-weight: 900;
		font-size: 24px;
		color: #fff;
	}

	.k-main-welcome--nav-btns {
		margin-top: 2vh;
		position: relative;
		z-index: 2;

		.k-main-welcome--nav-btn {
			width: 120px;
			height: 80px;
			padding-top: 5px;
			border-color: $page-background !important;
			border-width: 3px 3px 0 3px !important;
			border-style: solid;
			border-radius: 20px 20px 0 0;
			-webkit-box-shadow: none !important;
			box-shadow: none !important;
			margin: 0 10px;

			.v-icon {
				color: $page-background;
				font-size: 54px;

				&__component{
					height: 64px;
					width: 64px;
				}
			}
		}

		.k-main-welcome--nav-btn-current {
			border-color: transparent !important;
			background-color: $page-background !important;
		}
	}

	.k-main-welcome--pages {
		position: relative;
	}
}
</style>
